import chunk from 'lodash/chunk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLazyGetLinkedWorkItemQuery } from '~/api/linkedWorkItems/linkedWorkItems.api';
import {
  isLinkedWorkItemUri,
  parseLinkedWorkItemUri,
} from '~/api/linkedWorkItems/linkedWorkItems.helpers';
import { useSearchTasksMutation } from '~/redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import {
  getShowActiveTasks,
  getShowCompletedTasks,
} from '~redux/modules/tic/tasks/tic.tasks.selectors';
import { getTicTasks } from '~redux/modules/tic/tasks/tic.tasks.slice';
import {
  getContextualTicAccounts,
  getHasSettledWithoutTicWorkspace,
} from '~redux/modules/tic/tic.auth.selectors';
import { getHasLinkedWorkItemsSupport } from '~redux/selectors/accounts';

const BATCH_SIZE = 250;

/**
 * Hook to fetch linked resources that are missing from the Redux cache.
 * Uses searchTasks POST endpoint to batch fetch missing resources by ID.
 * Native linked-work uris (`timely://work-items/<id>`) are routed to the Rails
 * getLinkedWorkItem endpoint instead — they don't exist in TIC. A native id is fetchable directly,
 * so that lookup fires without waiting for the task pool to load, and only while the account has
 * the `api_linked_work` feature (without it the endpoint is not available to the account).
 * A TIC identifier waits for the pool's first load, unless both status toggles are off: then no
 * picker browse runs, so the pool may never load and the identifier is looked up straight away.
 * @param resourceIds - Array of resource IDs to ensure are loaded
 *
 * Returns `attemptedIdentifiers`: the set of IDs/URIs for which a fetch has
 * been completed (whether or not the resource was returned). Callers can use
 * this to stop showing a loading state when the backend has confirmed the
 * resource is not accessible (e.g. Jira connection without reference_tasks,
 * removed project subscription, or assignee restriction). `resolvingIdentifiers` is the subset
 * still on its way; `isResolving` is whether that subset is non-empty.
 */
type Props = { resourceIds: readonly string[] } | { resourceUris: readonly string[] };
export const useFetchMissingLinkedResources = (props: Props) => {
  const { workspace } = useSelector(getContextualTicAccounts);
  const allTasks = useSelector(getTicTasks);
  const [searchTasks] = useSearchTasksMutation();
  const [fetchLinkedWorkItem] = useLazyGetLinkedWorkItemQuery();
  const hasLinkedWorkItemsSupport = useSelector(getHasLinkedWorkItemsSupport);
  const hasSettledWithoutWorkspace = useSelector(getHasSettledWithoutTicWorkspace);
  const showActive = useSelector(getShowActiveTasks);
  const showCompleted = useSelector(getShowCompletedTasks);

  // Track which IDs we've already requested to avoid duplicate fetches
  const requestedIdsRef = useRef<Set<string>>(new Set());
  const requestedUrisRef = useRef<Set<string>>(new Set());

  // Track identifiers where a fetch attempt has settled (found or confirmed missing).
  // Exposed to callers so they can stop showing loading state for unresolvable resources.
  const [attemptedIdentifiers, setAttemptedIdentifiers] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  // TIC identifiers are only checked for being missing once the pool has loaded, so the lookup
  // doesn't re-fetch what the first browse page brings in anyway. With both status toggles off the
  // pickers' browse queries are skipped (`status: 'none'` cannot match), so nothing is coming to
  // load the pool and waiting for it would leave the identifier unresolved for good.
  const canLookUpTicIdentifiers = allTasks !== null || (!showActive && !showCompleted);
  let key: 'ids' | 'uris' = 'ids';
  if ('resourceUris' in props) {
    key = 'uris';
  }
  // Depend on the identifier arrays themselves, not `props` — callers pass a fresh props
  // object every render, which would defeat the memo (and re-run the fetch effect below).
  const resourceUris = 'resourceUris' in props ? props.resourceUris : undefined;
  const resourceIds = 'resourceIds' in props ? props.resourceIds : undefined;
  const identifiers = resourceUris ?? resourceIds ?? [];
  const cachedIdentifiers = useMemo(
    () => new Set((allTasks ?? []).map(t => (key === 'uris' ? t.uri : t.id))),
    [allTasks, key]
  );
  const missingIdentifiers = useMemo(() => {
    const requested = key === 'uris' ? requestedUrisRef.current : requestedIdsRef.current;
    return identifiers.filter(identifier => {
      if (cachedIdentifiers.has(identifier) || requested.has(identifier)) return false;
      if (isLinkedWorkItemUri(identifier)) return hasLinkedWorkItemsSupport;
      return canLookUpTicIdentifiers;
    });
  }, [identifiers, key, cachedIdentifiers, canLookUpTicIdentifiers, hasLinkedWorkItemsSupport]);

  // Fetch missing resources
  useEffect(() => {
    if (missingIdentifiers.length === 0) return;

    // Native linked-work synthetic uris resolve against the Rails API (no TIC workspace
    // required); everything else goes to TIC's searchTasks (which does require a workspace).
    // This split is mode-agnostic: in `resourceIds` mode a native item's option id IS its
    // synthetic uri (PresentableTaskResource.id === uri for native items), so native
    // identifiers can arrive in either mode and must always reach the Rails fetch — never TIC.
    const nativeUris = missingIdentifiers.filter(isLinkedWorkItemUri);
    const ticIdentifiers = missingIdentifiers.filter(uri => !isLinkedWorkItemUri(uri));

    const requests: Array<Promise<unknown>> = [];
    // Only identifiers we actually fire a request for are marked requested/attempted —
    // TIC identifiers stay eligible for a retry once a workspace becomes available.
    const firedIdentifiers: Array<string> = [];

    // Dedup tracking must use the ref the `missingIdentifiers` memo reads for the active mode
    // (`requestedIdsRef` in `resourceIds` mode, `requestedUrisRef` in `resourceUris` mode);
    // otherwise a still-in-flight native fetch would be re-fired on the next render.
    const markRequested = (identifier: string) => {
      if (key === 'uris') {
        requestedUrisRef.current.add(identifier);
      } else {
        requestedIdsRef.current.add(identifier);
      }
    };

    if (nativeUris.length > 0) {
      nativeUris.forEach(markRequested);
      firedIdentifiers.push(...nativeUris);
      requests.push(
        Promise.all(
          nativeUris.map(uri => {
            const id = parseLinkedWorkItemUri(uri);
            // Malformed synthetic uri — nothing to fetch; it still counts as attempted.
            if (id == null) return Promise.resolve();
            return fetchLinkedWorkItem({ id });
          })
        )
      );
    }

    if (ticIdentifiers.length > 0 && workspace?.id) {
      ticIdentifiers.forEach(markRequested);
      firedIdentifiers.push(...ticIdentifiers);
      requests.push(
        ...chunk(ticIdentifiers, BATCH_SIZE).map(batch =>
          searchTasks({
            body: {
              accountId: workspace.id,
              [key]: batch,
              limit: batch.length,
            },
          })
        )
      );
    }

    if (requests.length === 0) return;

    const markAttempted = () => {
      // Fetch settled (resources found or confirmed not accessible) — mark as attempted
      // so callers can stop showing a loading state for these identifiers.
      setAttemptedIdentifiers(prev => {
        const next = new Set(prev);
        firedIdentifiers.forEach(id => next.add(id));
        return next;
      });
    };

    Promise.all(requests)
      .then(markAttempted)
      .catch(() => {
        // Keep identifiers in requestedIdsRef/requestedUrisRef so they are not retried
        // (removing them would cause an infinite loop: re-render → memo re-includes them → re-fetch).
        // Still mark as attempted so callers clear the loading state immediately.
        // Do not re-throw — the promise is fire-and-forget inside a useEffect, so re-throwing
        // would produce an unhandled rejection in error monitoring with no benefit.
        markAttempted();
      });
  }, [workspace?.id, missingIdentifiers, key, searchTasks, fetchLinkedWorkItem]);

  // Resolution is tracked per identifier: one is still resolving until it is in the cache or its
  // fetch has settled (found, confirmed inaccessible, or failed) — and only while a lookup for it
  // can still happen. A native id is looked up directly whenever the account has the feature, so
  // without it nothing will fetch that id. A TIC identifier waits for the pool and a workspace, so
  // once the account has durably settled without a TIC workspace nothing will fetch it either.
  const resolvingIdentifiers = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        identifiers.filter(identifier => {
          if (cachedIdentifiers.has(identifier) || attemptedIdentifiers.has(identifier)) {
            return false;
          }
          if (isLinkedWorkItemUri(identifier)) return hasLinkedWorkItemsSupport;
          return !hasSettledWithoutWorkspace;
        })
      ),
    [
      identifiers,
      cachedIdentifiers,
      attemptedIdentifiers,
      hasLinkedWorkItemsSupport,
      hasSettledWithoutWorkspace,
    ]
  );

  return { attemptedIdentifiers, resolvingIdentifiers, isResolving: resolvingIdentifiers.size > 0 };
};
